import { supabase } from '../supabaseClient';

/**
 * Auth against Supabase Auth, plus resolution of the app-level role.
 *
 * An auth.users record on its own says nothing about what someone may do here.
 * The role comes from which table carries a row with that auth_user_id:
 * `therapists` (admin or professional) or `accounts` (patient). A user with
 * neither is authenticated but has no place in the app yet.
 */

export const ROLES = {
  ADMIN: 'admin',
  THERAPIST: 'therapist',
  PATIENT: 'patient',
};

export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) return { user: null, error };
  return { user: data.user, error: null };
};

export const signOut = () => supabase.auth.signOut();

export const getSession = async () => {
  const { data, error } = await supabase.auth.getSession();
  return { session: data?.session ?? null, error };
};

/**
 * Resolves the signed-in user to a role and their profile row.
 *
 * Returns a `blocked` result rather than a role when the profile is suspended,
 * so the UI can explain why instead of showing an empty dashboard.
 */
export const resolveUser = async (authUserId) => {
  const { data: therapist, error: therapistError } = await supabase
    .from('therapists')
    .select('*')
    .eq('auth_user_id', authUserId)
    .maybeSingle();

  if (therapistError) return { role: null, profile: null, error: therapistError };

  if (therapist) {
    // Checked before is_active: a pending applicant is inactive by design, and
    // telling them their account is "blocked" would be both wrong and alarming.
    if (therapist.approval_status === 'pending') {
      return { role: null, profile: therapist, pendingApproval: true, error: null };
    }
    if (therapist.approval_status === 'rejected') {
      return { role: null, profile: therapist, rejected: true, error: null };
    }
    if (!therapist.is_active || therapist.is_blocked) {
      return { role: null, profile: therapist, blocked: true, error: null };
    }
    return {
      role: therapist.is_admin ? ROLES.ADMIN : ROLES.THERAPIST,
      profile: therapist,
      error: null,
    };
  }

  const { data: account, error: accountError } = await supabase
    .from('accounts')
    .select('*')
    .eq('auth_user_id', authUserId)
    .maybeSingle();

  if (accountError) return { role: null, profile: null, error: accountError };

  if (account) {
    if (!account.is_active || account.is_blocked) {
      return { role: null, profile: account, blocked: true, error: null };
    }
    return { role: ROLES.PATIENT, profile: account, error: null };
  }

  return { role: null, profile: null, error: null };
};

/**
 * Registers a patient.
 *
 * The account row cannot be inserted until a session exists, because RLS checks
 * auth.uid() against auth_user_id — and with email confirmation enabled, signUp
 * returns no session. So the details travel in the auth user's metadata, which
 * Supabase stores server-side, and claim_patient_profile() builds the profile
 * on first sign-in.
 *
 * They deliberately do NOT go in localStorage. That assumed signup and
 * confirmation happen in the same browser; registering on a phone and opening
 * the email on a laptop lost the details entirely, leaving an account that
 * could authenticate but never become a patient.
 */
export const registerPatient = async ({ email, password, profile, therapistId, insurancePlanId }) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      // Where Supabase returns the user after they confirm.
      emailRedirectTo: window.location.origin,
      data: {
        pending_profile: true,
        first_name: profile.firstName,
        last_name: profile.lastName,
        phone: profile.phone,
        address: profile.address,
        nationality: profile.nationality,
        id_number: profile.idNumber,
        date_of_birth: profile.dateOfBirth,
        emergency_contact_name: profile.emergencyName,
        emergency_contact_phone: profile.emergencyPhone,
        emergency_contact_relation: profile.emergencyRelation,
        consent_signed: profile.consentSigned,
        wants_calendar_invites: profile.wantsCalendarInvites,
        therapist_id: therapistId || null,
        insurance_plan_id: insurancePlanId || null,
      },
    },
  });

  if (error) return { error, pendingConfirmation: false };

  // No session means confirmation is pending; the profile is built when they
  // come back, wherever they open the email.
  if (!data.session) return { error: null, pendingConfirmation: true };

  const { error: claimError } = await supabase.rpc('claim_patient_profile');
  return { error: claimError ?? null, pendingConfirmation: false };
};

